import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../auth";
import { Logo } from "../components/Logo";
import PublicMobileMenu from "../components/PublicMobileMenu";
import { Check, Clock, ArrowRight } from "lucide-react";

/*
 * SERVICES SUR MESURE — 3 prestations vendues à l'acte (hors abonnement)
 * Accessible publiquement ET depuis le dashboard connecté
 */
export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();
  const impressionSentRef = React.useRef(false);

  useEffect(() => {
    document.title = "Services sur mesure · AUPRÉA";
    let cancelled = false;
    api.get("/services").then((r) => {
      if (cancelled) return;
      const list = r.data.services || [];
      setServices(list);
      // Idempotent impression tracking : envoi 1 fois par session-navigation (protège de StrictMode)
      if (!impressionSentRef.current) {
        impressionSentRef.current = true;
        list.forEach((s) => {
          api.post("/services/track", { service_key: s.id, action: "impression", source: "services_page" }).catch(() => {});
        });
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handlePay = (svc) => {
    // Track click
    api.post("/services/track", { service_key: svc.id, action: "click", source: "services_page" }).catch(() => {});
    // Redirect to Stripe Payment Link
    let url = svc.stripe_payment_link;
    if (user?.email) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}prefilled_email=${encodeURIComponent(user.email)}`;
    }
    window.location.href = url;
  };

  return (
    <div style={{ backgroundColor: "var(--auprea-cream)", minHeight: "100vh" }} data-testid="services-page">
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <Link to="/"><Logo size={28} /></Link>
        <div className="flex items-center gap-4 md:gap-6">
          {!user ? (
            <>
              <Link to="/tarifs" style={{ color: "var(--auprea-navy)", fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }} className="hidden md:inline-block">Forfaits</Link>
              <Link to="/connexion" className="hidden md:inline-block"><button className="auprea-btn-outline" style={{ padding: "8px 20px", minHeight: 40 }}>Se connecter</button></Link>
              {/* Menu mobile (< 768px) : hamburger + drawer */}
              <PublicMobileMenu />
            </>
          ) : (
            <button onClick={() => nav("/carnet/identite")} style={{ color: "var(--auprea-blue)", fontSize: 14 }}>← Retour au carnet</button>
          )}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-14">
        <div className="text-center mb-14">
          <div style={{ color: "var(--auprea-gold)", fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 14 }}>
            — Services sur mesure
          </div>
          <h1 style={{ fontFamily: "Spectral, serif", fontSize: "clamp(30px, 4.5vw, 46px)", color: "var(--auprea-navy)", fontWeight: 500, marginBottom: 16, lineHeight: 1.15 }}>
            L'accompagnement humain à la carte
          </h1>
          <p style={{ fontSize: 17, color: "rgba(35,38,46,0.7)", maxWidth: 680, margin: "0 auto", lineHeight: 1.65 }}>
            Trois prestations conçues par nos coordinateurs pour vous accompagner sur les étapes clés. Vendues à l'acte, en complément (ou non) de votre abonnement AUPRÉA.
          </p>
        </div>

        {loading && <div style={{ textAlign: "center", color: "rgba(35,38,46,0.5)" }}>Chargement…</div>}

        <div className="grid md:grid-cols-3 gap-6" data-testid="services-grid">
          {services.map((svc) => (
            <div
              key={svc.id}
              className="p-7 flex flex-col"
              style={{ backgroundColor: "#fff", border: "1px solid rgba(21,41,79,0.12)", borderRadius: 6, transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
              data-testid={`service-${svc.id}`}
            >
              <div style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--auprea-gold)", fontWeight: 600, marginBottom: 8 }}>
                Prestation à l'acte
              </div>
              <h2 style={{ fontFamily: "Spectral, serif", fontSize: 22, color: "var(--auprea-navy)", fontWeight: 500, marginBottom: 8, lineHeight: 1.3, whiteSpace: "normal", overflowWrap: "break-word", hyphens: "auto" }}>
                {/* Empêche le guillemet fermant orphelin en fin de ligne */}
                {(svc.name || "").replace(/ »/g, "\u00A0»")}
              </h2>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
                <span style={{ fontFamily: "Spectral, serif", fontSize: 34, color: "var(--auprea-navy)", fontWeight: 500 }}>
                  {svc.price.toFixed(0)} €
                </span>
                {svc.duration && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
                    <Clock size={13} /> {svc.duration}
                  </span>
                )}
              </div>
              <p style={{ fontSize: 14, color: "rgba(35,38,46,0.75)", lineHeight: 1.6, marginBottom: 18 }}>
                {svc.long_description || svc.short_description}
              </p>
              <ul className="space-y-2 flex-1 mb-6">
                {(svc.highlights || []).map((h) => (
                  <li key={h} className="flex items-start gap-2" style={{ fontSize: 14, color: "var(--auprea-ink)" }}>
                    <Check size={15} color="var(--auprea-green)" style={{ marginTop: 3, flexShrink: 0 }} /> {h}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePay(svc)}
                style={{
                  width: "100%",
                  padding: "13px 20px",
                  background: "var(--auprea-gold)",
                  color: "var(--auprea-navy)",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  letterSpacing: "0.03em",
                }}
                data-testid={`btn-pay-${svc.id}`}
              >
                Réserver ({svc.price.toFixed(0)} €) <ArrowRight size={16} style={{ display: "inline", marginLeft: 6 }} />
              </button>
            </div>
          ))}
        </div>

        {/* Rassurance */}
        <div style={{ marginTop: 40, padding: "18px 22px", background: "rgba(21,41,79,0.05)", borderLeft: "3px solid var(--auprea-gold)", fontSize: 14, color: "rgba(35,38,46,0.75)", lineHeight: 1.6, maxWidth: 780, marginLeft: "auto", marginRight: "auto" }}>
          <strong style={{ color: "var(--auprea-navy)" }}>Paiement sécurisé Stripe · Facture envoyée automatiquement.</strong>{" "}
          Après paiement, un coordinateur AUPRÉA vous recontacte sous 24 h pour planifier votre rendez-vous.
        </div>
      </main>

      <footer className="text-center py-8 border-t" style={{ borderColor: "rgba(21,41,79,0.08)", color: "rgba(35,38,46,0.55)", fontSize: 12 }}>
        AUPRÉA · <em>Anticiper est un acte d'amour.</em>
      </footer>
    </div>
  );
}
